// @ts-check
/* eslint-env browser */

/** @typedef {import('@geoman-io/maplibre-geoman-free').GeoJsonImportFeature} GeoJsonImportFeature */

import * as turf from '@turf/turf'
import { reactiveStore } from '../reactive-store'
import { getGeomanFeatureByPropertyId } from './edit-feature'

/**
 * Handles merging geometries when in 'add' mode.
 * When a new feature is drawn in add mode, it merges it with the existing selected feature.
 *
 * @param {import('@geoman-io/maplibre-geoman-free').Geoman} geoman - The Geoman instance
 * @param {string} eventName - The name of the event
 * @param {any} featureData - The feature data from the event
 * @returns {boolean} Returns true if the merge was handled, false otherwise
 */
export function mergeGeometriesForAddMode (geoman, eventName, featureData) {
  // Check if this is a draw:feature_created event and we're in add or add_point mode
  const editMode = reactiveStore.editMode.get()
  if ((eventName !== 'gm:draw:feature_created' && eventName !== '_gm:draw:feature_created') ||
      (editMode !== 'add' && editMode !== 'add_point') ||
      !geoman) {
    return false
  }

  // @ts-ignore - featureData exists on draw:feature_created events
  if (!featureData?._geoJson) {
    return false
  }

  const selectedFeatureId = reactiveStore.selectedFeatureId.get()
  const existingGeomanFeature = getGeomanFeatureByPropertyId(geoman, selectedFeatureId)
  const newGeometry = featureData._geoJson.geometry

  // If feature is not in Geoman, check if it exists in reactiveStore (null geometry case)
  if (!existingGeomanFeature) {
    const existingReactiveFeature = reactiveStore.features.get().find(
      feature => feature.properties?._id === selectedFeatureId
    )

    if (!existingReactiveFeature) {
      return false
    }

    // Feature has null or empty geometry - assign the new geometry directly
    const newFeature = {
      type: 'Feature',
      geometry: newGeometry,
      properties: { ...existingReactiveFeature.properties }
    }
    delete newFeature.properties.__gm_id

    const updatedFeatures = reactiveStore.features.get().map(feature =>
      feature.properties?._id === selectedFeatureId ? newFeature : feature
    )

    reactiveStore.features.set(updatedFeatures)

    // Cleanup is handled by #showFeatures when it re-imports all features
    return true
  }

  const existingGeoJson = existingGeomanFeature.getGeoJson()

  let merged
  try {
    merged = turf.union(turf.featureCollection([turf.feature(existingGeoJson.geometry), turf.feature(newGeometry)]))
    if (!merged?.geometry) {
      return false
    }
  } catch (error) {
    // If merge fails (e.g., empty geometry), just use the new geometry
    const newFeature = {
      type: 'Feature',
      geometry: newGeometry,
      properties: { ...existingGeoJson.properties }
    }
    delete newFeature.properties.__gm_id

    const updatedFeatures = reactiveStore.features.get().map(feature =>
      feature.properties?._id === selectedFeatureId ? newFeature : feature
    )

    reactiveStore.features.set(updatedFeatures)
    return true
  }

  // Create merged feature with existing properties
  /** @type {GeoJsonImportFeature} */
  const mergedFeature = {
    type: 'Feature',
    geometry: merged.geometry,
    properties: { ...existingGeoJson.properties }
  }
  delete mergedFeature.properties.__gm_id

  // Get current features from reactiveStore and replace the existing one with merged
  const updatedFeatures = reactiveStore.features.get().map(feature =>
    feature.properties?._id === reactiveStore.selectedFeatureId.get() ? mergedFeature : feature
  )

  // Update reactiveStore - this will automatically trigger #showFeatures
  reactiveStore.features.set(updatedFeatures)
  return true
}
